import React, { useState, useEffect, useRef } from 'react';
import './Process.css';

function Process() {
  const [visibleSteps, setVisibleSteps] = useState([]);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [connectorProgress, setConnectorProgress] = useState([]);
  const [activeSteps, setActiveSteps] = useState([]);
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const stepRefs = useRef([]);

  const steps = [
    {
      number: '01',
      title: 'Discovery & Strategy',
      description: 'We dive deep into your business, understanding your goals, target audience, and competitive landscape to create a solid foundation.',
      duration: '1 week',
      deliverables: ['Business analysis', 'Competitor research', 'Strategy document', 'Project roadmap']
    },
    {
      number: '02',
      title: 'Design & Prototyping',
      description: 'Our designers craft beautiful, conversion-focused designs that align with your brand and resonate with your customers.',
      duration: '2-3 weeks',
      deliverables: ['Wireframes', 'Visual designs', 'Interactive prototype', 'Design system']
    },
    {
      number: '03',
      title: 'Development',
      description: 'We bring designs to life with clean, optimized code, ensuring your store is fast, secure, and scalable.',
      duration: '3-4 weeks',
      deliverables: ['Custom theme', 'Feature integration', 'Quality assurance', 'Performance optimization']
    },
    {
      number: '04',
      title: 'Launch & Optimize',
      description: 'We carefully launch your store and continuously monitor performance, making data-driven optimizations for maximum results.',
      duration: 'Ongoing',
      deliverables: ['Store launch', 'Analytics setup', 'Training session', 'Post-launch support']
    }
  ];

  // Show first step immediately when section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Show only the first step initially
            setVisibleSteps([0]);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Scroll-based vertical connector progress and step reveal
  useEffect(() => {
    const handleScroll = () => {
      if (stepRefs.current.length === 0) return;

      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const viewportBottom = scrollY + viewportHeight;
      const triggerOffset = 200; // Trigger when element is 200px from bottom of viewport

      const newConnectorProgress = [];
      const newActiveSteps = [];
      const newVisibleSteps = [...visibleSteps];

      stepRefs.current.forEach((stepElement, index) => {
        if (!stepElement) return;

        const stepRect = stepElement.getBoundingClientRect();
        const stepTop = stepElement.offsetTop; // Absolute position from top of page
        const stepScreenTop = stepRect.top; // Position relative to viewport

        // Only allow revealing the next step in sequence
        const maxVisibleIndex = visibleSteps.length > 0 ? Math.max(...visibleSteps) : -1;
        const isNextInSequence = index === maxVisibleIndex + 1;

        // Make step visible when:
        // 1. It's the next step in sequence
        // 2. Its top is entering the viewport (less than viewport height)
        const isEnteringViewport = stepScreenTop < (viewportHeight - 100);

        if (isNextInSequence && isEnteringViewport && !visibleSteps.includes(index)) {
          console.log(`Revealing step ${index + 1}, stepScreenTop: ${stepScreenTop}, viewportHeight: ${viewportHeight}`);
          newVisibleSteps.push(index);
        }

        // Activate step when viewport middle passes it
        const viewportMiddle = scrollY + (viewportHeight / 2);
        if (visibleSteps.includes(index) && viewportMiddle >= stepTop) {
          newActiveSteps.push(index);
        }

        // Calculate connector fill progress only for visible steps
        if (index < steps.length - 1 && visibleSteps.includes(index)) {
          const nextStepIndex = index + 1;
          const nextStep = stepRefs.current[nextStepIndex];

          if (nextStep) {
            // Connector starts after current step content
            const connectorStart = stepTop + stepRect.height;
            // Connector ends at next step start
            const connectorEnd = nextStep.offsetTop;
            const connectorLength = connectorEnd - connectorStart;

            // How far the viewport bottom has traveled into this connector
            const viewportMiddle = scrollY + (viewportHeight / 2);
            const distanceTraveled = viewportMiddle - connectorStart;
            let progress = (distanceTraveled / connectorLength) * 100;

            // Clamp between 0 and 100
            progress = Math.max(0, Math.min(100, progress));

            newConnectorProgress[index] = progress;
          }
        }
      });

      // Debug logging
      if (newConnectorProgress.length > 0) {
        console.log('Connector Progress:', newConnectorProgress);
        console.log('Visible Steps:', newVisibleSteps);
      }

      setConnectorProgress(newConnectorProgress);
      setActiveSteps(newActiveSteps);

      // Update visible steps if changed
      if (newVisibleSteps.length !== visibleSteps.length) {
        setVisibleSteps(newVisibleSteps);
      }
    };

    // Use scroll event for real-time updates
    const onScroll = () => {
      handleScroll();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    // Initial calculation to check if steps should be visible on mount/update
    const timer = setTimeout(() => {
      handleScroll();
      console.log('Initial scroll check, visible steps:', visibleSteps);
    }, 100);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', handleScroll);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSteps]);

  return (
    <section id="process" className="process-section" ref={sectionRef}>
      <div className="container">
        <div className="section-header fade-in">
          <div className="section-badge">How We Work</div>
          <h2 className="section-title">
            Our Proven
            <span className="gradient-text"> Design Process</span>
          </h2>
          <p className="section-description">
            A streamlined, collaborative approach that ensures your project is delivered
            on time, on budget, and exceeds expectations at every stage.
          </p>
        </div>

        <div className="process-timeline" ref={timelineRef}>
          {steps.map((step, index) => {
            const isVisible = visibleSteps.includes(index);
            const connectorProgressValue = connectorProgress[index] || 0;

            return (
            <div
              key={index}
              ref={el => stepRefs.current[index] = el}
              className={`process-step ${isVisible ? 'visible' : ''} ${hoveredStep === index ? 'hovered' : ''} ${activeSteps.includes(index) ? 'scroll-active' : ''}`}
              onMouseEnter={() => setHoveredStep(index)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <div className="step-number-wrapper">
                <div className="number-background">
                  <div className="number-glow"></div>
                  <div className="number-particles">
                    <span className="particle particle-1"></span>
                    <span className="particle particle-2"></span>
                    <span className="particle particle-3"></span>
                    <span className="particle particle-4"></span>
                  </div>
                </div>
                <div className="step-number">
                  <span className="number-text">{step.number}</span>
                  <div className="number-ring"></div>
                  <div className="number-ring-outer"></div>
                </div>
              </div>

              <div className="step-content">
                <div className="content-shimmer"></div>

                <div className="step-header">
                  <h3 className="step-title">
                    {step.title.split(' ').map((word, idx) => (
                      <span key={idx} className="title-word" style={{ animationDelay: `${idx * 0.1}s` }}>
                        {word}{' '}
                      </span>
                    ))}
                  </h3>
                  <span className="step-duration">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 14.5C11.5899 14.5 14.5 11.5899 14.5 8C14.5 4.41015 11.5899 1.5 8 1.5C4.41015 1.5 1.5 4.41015 1.5 8C1.5 11.5899 4.41015 14.5 8 14.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 4V8L10.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {step.duration}
                  </span>
                </div>

                <p className="step-description">{step.description}</p>

                <div className="step-deliverables">
                  <h4>Deliverables:</h4>
                  <ul>
                    {step.deliverables.map((deliverable, idx) => (
                      <li key={idx} style={{ transitionDelay: `${idx * 0.1}s` }}>
                        <span className="check-icon">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        <span className="deliverable-text">{deliverable}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {index < steps.length - 1 && (
                <div
                  className="step-connector"
                  data-progress={connectorProgressValue.toFixed(1)}
                  data-connector={`${index}-to-${index + 1}`}
                >
                  <div className="connector-line-background"></div>
                  <div
                    className="connector-line-fill"
                    style={{
                      transform: `scaleY(${connectorProgressValue / 100})`
                    }}
                  ></div>
                  <div
                    className="connector-progress-dot"
                    style={{
                      top: `${connectorProgressValue}%`,
                      opacity: connectorProgressValue > 0 && connectorProgressValue < 100 ? 1 : 0
                    }}
                  ></div>
                </div>
              )}
            </div>
            );
          })}
        </div>

        <div className="process-cta fade-in">
          <div className="cta-background">
            <div className="cta-glow"></div>
            <div className="cta-grid"></div>
          </div>
          <div className="cta-content">
            <h3>Ready to start your project?</h3>
            <p>Let's discuss how we can help transform your e-commerce vision into reality.</p>
          </div>
          <button
            className="btn-primary btn-large magnetic"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="btn-text">Schedule a Consultation</span>
            <span className="btn-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}

export default Process;
